import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { FiCheckCircle, FiRefreshCw, FiX } from 'react-icons/fi';
import { useHydrationStore } from '../../stores/hydrationStore';
import { useArtifactoryStore } from '../../stores/artifactoryStore';
import { DependencyGraph } from './hydration/DependencyGraph';
import { HydrationControls } from './hydration/HydrationControls';
import { HydrationPlanTable } from './hydration/HydrationPlanTable';
import { HydrationProgress } from './hydration/HydrationProgress';
interface HydrationPlanPanelProps {
  manifestId: string;
  sessionId: string;
  focusNodeId?: string;
  onComplete?: () => void;
  onError?: (error: any) => void;
  maxHeight?: string;
  forceRender?: boolean;
}

export const HydrationPlanPanel: React.FC<HydrationPlanPanelProps> = ({ 
  manifestId: propManifestId, 
  sessionId, 
  focusNodeId,
  onComplete,
  onError,
  maxHeight = '400px',
  forceRender = false
}) => {
  const { darkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  
  // Get manifest from artifactory store
  const { manifestId: storeManifestId, manifest } = useArtifactoryStore();
  
  // Use the hydration store for all hydration-related state
  const {
    initialize,
    buildHydrationPlan,
    hydrationPlan,
    isLoading,
    error,
    selectedNodes,
    currentlyHydrating,
    isAutoHydrating,
    completedNodes,
    failedNodes,
    nodeStatuses,
    nodeErrors,
    selectNode,
    selectAllNodes,
    clearSelection,
    hydrateNode,
    startAutoHydration,
    stopAutoHydration
  } = useHydrationStore();
  
  // Add state to store the focus callback
  const [focusNodeInGraph, setFocusNodeInGraph] = useState<((nodeId: string) => void) | undefined>(undefined);

  // Add a handler to register the focus callback from the graph
  const handleFocusNodeCallback = useCallback((callback: (nodeId: string) => void) => {
    // Store the callback without modification - we don't need to modify it here
    setFocusNodeInGraph(() => callback);
  }, []);
  
  // Always show all files and directories by default
  const showAllNodes = true;
  const showAllDirectories = true;
  
  // Use the store's manifest ID if available, otherwise fall back to the prop
  const effectiveManifestId = storeManifestId || propManifestId;

  // Use ref to track initialization state
  const initRef = useRef('');
  const isInitializedRef = useRef(false);
  const mountCountRef = useRef(0);

  // Track component mounting state
  const isComponentMounted = useRef(true);
  
  useEffect(() => {
    isComponentMounted.current = true;
    return () => {
      isComponentMounted.current = false;
      // Ensure we stop auto-hydration when component unmounts
      if (isAutoHydrating) {
        console.log("Stopping auto-hydration on unmount");
        stopAutoHydration();
      }
    };
  }, [isAutoHydrating, stopAutoHydration]);

  // Initialize the hydration store when the component mounts or when key dependencies change
  useEffect(() => {
    // Skip initialization if component is not mounted
    if (!isComponentMounted.current) return;
    
    // Force re-initialization when focusNodeId changes
    if (focusNodeId && isInitializedRef.current) {
      console.log(`Focus node changed to ${focusNodeId}, re-initializing hydration store`);
      isInitializedRef.current = false; // Reset to force re-initialization
    }
    
    // Only initialize once per component instance or when focusNodeId changes
    if (isInitializedRef.current) {
      console.log("Component already initialized, skipping");
      return;
    }
    
    // Create a unique key for this initialization set of parameters
    const initKey = `${effectiveManifestId}-${sessionId}-${focusNodeId || 'all'}-${mountCountRef.current}`;
    
    // If we've already initialized with this exact key, skip
    if (initRef.current === initKey) {
      console.log(`Already initialized with key ${initKey}, skipping`);
      return;
    }
    
    console.log(`Initializing hydration store with manifestId: ${effectiveManifestId}, sessionId: ${sessionId}, focusNodeId: ${focusNodeId || 'all'}, mountCount: ${mountCountRef.current}`);
    
    // Set the refs BEFORE initialization to prevent loops
    initRef.current = initKey;
    isInitializedRef.current = true;
    
    // Use a local variable to track if this effect instance is still active
    let isEffectActive = true;
    
    const initializeStore = async () => {
      try {
        if (effectiveManifestId && sessionId) {
          // If no focusNodeId is provided, initialize without a focus node
          // This will show all nodes in the project for the root view
          if (isEffectActive && isComponentMounted.current) {
            await initialize(effectiveManifestId, sessionId, focusNodeId);
          }
        } else if (manifest && isEffectActive && isComponentMounted.current) {
          console.log(`Building local hydration plan from manifest`);
          await buildHydrationPlan(manifest);
        }
      } catch (error) {
        if (isEffectActive && isComponentMounted.current) {
          console.error("Error initializing hydration store:", error);
        }
      }
    };
    
    initializeStore();
    
    // Cleanup function to reset initialization state and mark effect as inactive
    return () => {
      isEffectActive = false;
    };
  }, [effectiveManifestId, sessionId, focusNodeId]); // Simplified dependencies


  // Add a semaphore to prevent infinite loops
  const completionSemaphore = useRef(false);
  
  // Reset semaphore when selection changes
  useEffect(() => {
    completionSemaphore.current = false;
  }, [selectedNodes.length]); // Reset when selection count changes
  
  // Call onComplete when all selected nodes are hydrated
  useEffect(() => {
    if (isComponentMounted.current && 
        onComplete && 
        hydrationPlan && 
        selectedNodes.length > 0 && 
        selectedNodes.every(id => completedNodes.has(id)) &&
        !completionSemaphore.current) { // Check semaphore
      console.log("All selected nodes hydrated, calling onComplete");
      completionSemaphore.current = true; // Set semaphore
      onComplete();
    }
  }, [hydrationPlan, selectedNodes, completedNodes, onComplete]);

  // Call onError when there's an error
  useEffect(() => {
    if (error && onError) {
      onError(new Error(error));
    }
  }, [error, onError]);

  // This function is already defined by the hydration store

  // Disable this logging effect
  /*
  useEffect(() => {
    if (hydrationPlan) {
      console.log(`Hydration plan loaded:`, {
        nodes_to_hydrate: hydrationPlan.nodes_to_hydrate,
        ordered_plan_length: hydrationPlan.ordered_plan?.length || 0,
        focus_node_id: hydrationPlan.focus_node_id || 'none'
      });
    }
  }, [hydrationPlan?.focus_node_id]); // Only re-run when focus_node_id changes
  */

  // Auto-start hydration if specified
  useEffect(() => {
    // Only monitor isAutoHydrating state, don't call startAutoHydration again
    // This prevents an infinite loop
    if (isComponentMounted.current && isAutoHydrating && hydrationPlan && 
        hydrationPlan.ordered_plan && selectedNodes.length > 0) {
      console.log("Auto-hydration is running");
      // Don't call startAutoHydration() here - it's already started
    }
    
    // No need for cleanup here as it's handled in the component mount effect
  }, [isAutoHydrating, hydrationPlan, selectedNodes]); // Remove startAutoHydration and stopAutoHydration from dependencies


  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        <span className="ml-2">Loading hydration plan...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`p-4 rounded-md ${darkMode ? 'bg-red-900 bg-opacity-20 text-red-400' : 'bg-red-100 text-red-700'}`}>
        <h2 className="text-lg font-semibold mb-2">Error</h2>
        <p>{error}</p>
        {error.includes('Manifest not found') && (
          <div className="mt-4">
            <p className="text-sm">Possible solutions:</p>
            <ul className="list-disc pl-5 mt-2 text-sm">
              <li>Refresh the page to reload the project data</li>
              <li>Check if the project still exists</li>
              <li>Try creating a new project if this one is no longer available</li>
            </ul>
          </div>
        )}
      </div>
    );
  }

  // Check if there are actually nodes in the ordered plan
  const hasNodesToHydrate = hydrationPlan && 
    hydrationPlan.ordered_plan && 
    hydrationPlan.ordered_plan.length > 0;

  if (!hydrationPlan) {
    return null;
  }

  if (!hasNodesToHydrate) {
    // Even if there are no dependencies to hydrate, we still show the panel
    // with the option to hydrate the current node
    const currentNode = focusNodeId && manifest?.nodes ? manifest.nodes[focusNodeId] : null;
    
    return (
      <div className={`border rounded-md ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
        <div className={`p-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex justify-between items-center`}>
          <div>
            <h2 className="font-semibold">
              {focusNodeId ? 'Hydration Plan' : 'Project Hydration Plan'}
            </h2>
            <div className="text-xs text-gray-400">
              No dependencies need hydration
            </div>
          </div>
        </div>
        
        <div className="p-4">
          {currentNode ? (
            <div className="flex flex-col items-center justify-center py-6">
              <div className={`p-3 rounded-full ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-100'} mb-4`}>
                <FiRefreshCw className={`h-6 w-6 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
              <h3 className="text-lg font-semibold mb-2">Ready to Hydrate</h3>
              
              {currentNode.type === 'directory' ? (
                <div className="text-center mb-4 max-w-md">
                  <p className="mb-2">
                    This is a directory. Directories themselves are not hydrated, but you can hydrate all files within this directory.
                  </p>
                  <p>
                    Use the hydration controls below to select and hydrate specific files.
                  </p>
                </div>
              ) : (
                <p className="text-center mb-4 max-w-md">
                  This file has no dependencies that need hydration. You can proceed with hydrating this file directly.
                </p>
              )}
              
              {currentNode.type !== 'directory' && (
                <button
                  className={`px-4 py-2 rounded text-sm font-medium ${
                    darkMode ? 'bg-blue-700 hover:bg-blue-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                  onClick={() => {
                    if (onComplete) {
                      onComplete();
                    }
                  }}
                >
                  Hydrate {currentNode.path.split('/').pop()}
                </button>
              )}
            </div>
          ) : (
            <div className={`p-4 rounded-md ${darkMode ? 'bg-green-900 bg-opacity-20 text-green-400' : 'bg-green-100 text-green-700'}`}>
              <h2 className="text-lg font-semibold mb-2 flex items-center">
                <FiCheckCircle className="mr-2" />
                Project Fully Hydrated
              </h2>
              <p>All files in this project are already hydrated. You can export the project now.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main component with all subcomponents
  return (
    <div className={`flex flex-col h-full overflow-hidden border rounded-md ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
      {/* Pinned header */}
      <div className={`flex-shrink-0 p-2 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex justify-between items-center`}>
        <div className="flex items-center space-x-2">
          <h2 className="font-semibold text-sm">
            {focusNodeId ? 'Dependency Hydration' : 'Project Hydration'}
          </h2>
          <span className={`text-xs px-1.5 py-0.5 rounded-full ${
            darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'
          }`}>
            {hydrationPlan?.ordered_plan?.length > 0 
              ? `${hydrationPlan.total_nodes || hydrationPlan.ordered_plan.length} files (${hydrationPlan.nodes_to_hydrate} need hydration)`
              : 'No files to hydrate'}
          </span>
        </div>
        
        {/* Hydration Controls */}
        <HydrationControls
          isAutoHydrating={isAutoHydrating}
          selectedNodesCount={selectedNodes.length}
          onSelectAll={selectAllNodes}
          onClearSelection={clearSelection}
          onStartAutoHydration={startAutoHydration}
          onStopAutoHydration={stopAutoHydration}
        />
      </div>
      
      {/* Stats bar - pinned below header */}
      <div className={`flex-shrink-0 px-2 py-1 text-xs ${darkMode ? 'text-gray-400 bg-gray-750' : 'text-gray-600 bg-gray-50'} border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        {hydrationPlan.ordered_plan.length} nodes in {hydrationPlan.ordered_plan.length > 0 ? 'multiple' : '0'} groups
        {Object.entries(hydrationPlan.ordered_plan.reduce((acc, node) => {
          const status = nodeStatuses[node.id] || node.status;
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        }, {})).map(([status, count]) => (
          <span key={status} className="ml-2">
            <span className={getStatusColor(status)}>{status}</span>: {count}
          </span>
        ))}
        {showAllNodes && manifest?.nodes && (
          <span className="ml-2">
            • Showing all {Object.keys(manifest.nodes).filter(id => 
              manifest.nodes[id].type !== 'directory' || showAllDirectories
            ).length} project files
            {showAllDirectories && ` and directories`}
          </span>
        )}
      </div>
      
      {/* Scrollable middle */}
      <div className="flex-1 overflow-y-auto min-h-0">

      {/* Dependency Graph */}
      {focusNodeId && (
        <DependencyGraph
          key={`graph-${focusNodeId}`} // Add key to force re-render when focusNodeId changes
          focusNodeId={focusNodeId}
          manifest={manifest}
          nodeStatuses={nodeStatuses}
          completedNodes={completedNodes}
          maxHeight="200px" // Reduced height
          hydrationPlan={hydrationPlan} // Pass the hydration plan data
          currentlyHydrating={currentlyHydrating} // Make sure we're passing this
          failedNodes={failedNodes} // Make sure we're passing this
          onFocusNode={handleFocusNodeCallback} // Pass the callback handler
        />
      )}
      
      {/* Hydration Plan Table */}
      <HydrationPlanTable
        hydrationPlan={hydrationPlan}
        selectedNodes={selectedNodes}
        currentlyHydrating={currentlyHydrating}
        completedNodes={completedNodes}
        failedNodes={failedNodes}
        nodeStatuses={nodeStatuses}
        nodeErrors={nodeErrors}
        isAutoHydrating={isAutoHydrating}
        manifest={manifest}
        onSelectNode={selectNode}
        onHydrateNode={hydrateNode}
        onSelectAll={selectAllNodes}
        onClearSelection={clearSelection}
        maxHeight={maxHeight}
        onFocusNodeInGraph={focusNodeInGraph}
        isRootNodeView={!focusNodeId}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm} // Pass the search change handler
      />
      
      </div>{/* end scrollable middle */}

      {/* Pinned footer */}
      <div className="flex-shrink-0">
        <HydrationProgress
          completedCount={completedNodes.size}
          failedCount={failedNodes.size}
          totalCount={hydrationPlan.nodes_to_hydrate}
          currentlyHydrating={currentlyHydrating}
        />
      </div>
    </div>
  );
};

// Helper function to get status color
const getStatusColor = (status: string): string => {
  switch (status) {
    case 'ghost': return 'text-gray-400';
    case 'pending': return 'text-yellow-500';
    case 'hydrating': return 'text-blue-500';
    case 'fresh': return 'text-green-500';
    case 'stale': return 'text-orange-500';
    case 'locked': return 'text-red-500';
    default: return 'text-gray-400';
  }
};
